/**
 * PriorityPanel — "Today's priority" task list derived from workspace
 * signals.
 *
 * Tasks aren't stored anywhere — they're computed in this component from
 * the same `workspaceSignals` payload the health-signal row reads. The
 * derivation is deterministic so the operator and the founder see the
 * same list at the same instant: the source of truth is the data, not
 * a stateful task table.
 *
 * Rules (in priority order):
 *   1. Big overdue → "Push the biggest overdue customer today"
 *      (focus card at the top — only when an overdue customer exists).
 *   2. Petty drawer below 30 % of imprest → "Reimburse Office Drawer".
 *   3. Bank match unmatched > 0 → "Match N rows on <account>".
 *   4. TDS due within 30 days OR past due → "Prepare TDS challan".
 *   5. Pending approvals > 0 → "Review N approvals".
 *
 * If none apply, the panel renders an empty "everything green" state.
 *
 * @see prototype/home/dashboard.html `.focus` + `.task` rows — V1.1 baseline
 */
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Card } from "@flint/ui";
import { paiseToInr, paiseToInrShort } from "../../../lib/format.ts";

export interface PriorityPanelProps {
  topOverdue: {
    customerName: string;
    outstandingPaise: string;
    oldestDays: number;
    customerId: string;
  } | null;
  pettyCash: {
    drawerId: string | null;
    drawerName: string | null;
    balancePaise: string;
    imprestPaise: string;
  };
  bankMatch: {
    unmatchedCount: number;
    primaryAccountName: string | null;
    primaryAccountId: string | null;
  };
  tds: { depositDuePaise: string; daysUntilDue: number; quarter: string };
  approvals: { pendingCount: number; totalAtStakePaise: string };
}

export function PriorityPanel(props: PriorityPanelProps) {
  const tasks = computeTasks(props);

  return (
    <Card className="min-w-0">
      <header className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-line">
        <div>
          <h3 className="text-sm font-semibold text-ink">Today’s priority</h3>
          <p className="text-xs text-muted">Where leverage is highest.</p>
        </div>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-ok-50 text-ok">
          <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current" />
          Recommended
        </span>
      </header>

      <div className="p-4 space-y-3">
        {props.topOverdue ? (
          <FocusCard
            label="First action"
            title={`Push ${props.topOverdue.customerName} today.`}
            body={`${describeOverdue(props.topOverdue.oldestDays)} · ${paiseToInr(
              props.topOverdue.outstandingPaise,
            )} carries most of the drag. Clearing this one tilts your week.`}
            href={`/sales/customers/${props.topOverdue.customerId}`}
          />
        ) : null}

        {tasks.length === 0 ? (
          <p className="px-1 py-4 text-sm text-muted">
            Nothing is screaming for attention. Tidy a draft, or call it a day.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {tasks.map((task) => (
              <TaskRow key={task.key} task={task} />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

// ── derivation ─────────────────────────────────────────────────────

interface DerivedTask {
  key: string;
  title: string;
  detail: string;
  href: string;
  when?: string;
}

/**
 * Translate workspace signals into a ranked task list. Pure function —
 * same inputs always produce the same output, so server + client agree.
 */
function computeTasks(props: PriorityPanelProps): DerivedTask[] {
  const tasks: DerivedTask[] = [];

  // 1. Top overdue. Reuses the focus-card hero so the row form is a
  //    secondary nudge — "and remind the next one".
  if (props.topOverdue !== null) {
    tasks.push({
      key: "collect-top-overdue",
      title: `Send reminder · ${props.topOverdue.customerName}`,
      detail: `WhatsApp template pre-filled · ${paiseToInr(
        props.topOverdue.outstandingPaise,
      )} owed`,
      href: `/sales/customers/${props.topOverdue.customerId}`,
      when: "10:30",
    });
  }

  // 2. Petty drawer — only when imprest is set AND balance is below
  //    30 % of ceiling (matches the health-card amber threshold).
  const pettyImprest = BigInt(props.pettyCash.imprestPaise || "0");
  const pettyBalance = BigInt(props.pettyCash.balancePaise || "0");
  if (
    props.pettyCash.drawerId !== null &&
    pettyImprest > 0n &&
    pettyBalance * 10n < pettyImprest * 3n
  ) {
    const topUp = pettyImprest - pettyBalance;
    tasks.push({
      key: "reimburse-petty",
      title: `Reimburse ${props.pettyCash.drawerName ?? "Office Drawer"}`,
      detail: `Top up by ${paiseToInr(topUp.toString())} to restore the imprest float.`,
      href: `/cash/bank-accounts/${props.pettyCash.drawerId}`,
      when: "11:45",
    });
  }

  // 3. Bank match — surface oldest-since copy is on the health card; the
  //    task here is the "go match them" verb.
  if (props.bankMatch.unmatchedCount > 0) {
    tasks.push({
      key: "match-bank",
      title: `Match ${String(props.bankMatch.unmatchedCount)} statement row${
        props.bankMatch.unmatchedCount === 1 ? "" : "s"
      }`,
      detail: props.bankMatch.primaryAccountName
        ? `${props.bankMatch.primaryAccountName} · workbench is ready`
        : "Workbench is ready",
      href: "/cash/reconcile",
      when: "14:00",
    });
  }

  // 4. TDS — surface only when there's something collected AND the due
  //    date is within a month (or already past).
  const tdsDue = BigInt(props.tds.depositDuePaise || "0");
  if (tdsDue > 0n && props.tds.daysUntilDue <= 30) {
    tasks.push({
      key: "prepare-tds",
      title: `Prepare TDS challan · ${props.tds.quarter}`,
      detail:
        props.tds.daysUntilDue >= 0
          ? `${paiseToInrShort(props.tds.depositDuePaise)} due in ${String(props.tds.daysUntilDue)} day${
              props.tds.daysUntilDue === 1 ? "" : "s"
            }`
          : `${paiseToInrShort(props.tds.depositDuePaise)} · ${String(Math.abs(props.tds.daysUntilDue))} day${
              Math.abs(props.tds.daysUntilDue) === 1 ? "" : "s"
            } past due`,
      href: "/compliance/tds",
      when: "15:30",
    });
  }

  // 5. Approvals — last, because they're the most disposable while still
  //    being load-bearing for the operator who's blocked on the founder.
  if (props.approvals.pendingCount > 0) {
    const stake = BigInt(props.approvals.totalAtStakePaise || "0");
    tasks.push({
      key: "review-approvals",
      title: `Review ${String(props.approvals.pendingCount)} approval${
        props.approvals.pendingCount === 1 ? "" : "s"
      }`,
      detail:
        stake > 0n
          ? `${paiseToInr(props.approvals.totalAtStakePaise)} of vendor payments waiting on your OTP`
          : "Operator-flagged items waiting on your OTP",
      href: "/approvals",
      when: "16:30",
    });
  }

  return tasks;
}

// ── presentational pieces ──────────────────────────────────────────

function FocusCard({
  label,
  title,
  body,
  href,
}: {
  label: string;
  title: string;
  body: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className="block bg-sky-50 border border-sky-100 border-l-[3px] border-l-sky-600 rounded-sm px-4 py-3 hover:bg-sky-100 transition-colors no-underline"
    >
      <span className="inline-block text-xs font-bold uppercase tracking-wider text-sky-700 mb-1">
        {label}
      </span>
      <strong className="block text-sm font-semibold text-ink">{title}</strong>
      <p className="mt-1 text-xs text-muted leading-relaxed">{body}</p>
    </a>
  );
}

function TaskRow({ task }: { task: DerivedTask }) {
  return (
    <li className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
      <span
        aria-hidden
        className="flex-shrink-0 mt-0.5 w-[18px] h-[18px] border-[1.5px] border-line-2 rounded bg-paper inline-grid place-items-center"
      >
        <CheckCircle2 size={10} className="text-transparent" />
      </span>
      <div className="flex-1 min-w-0">
        <strong className="block text-xs font-semibold text-ink">{task.title}</strong>
        <a
          href={task.href}
          className="text-xs text-muted hover:text-ink-2 inline-flex items-center gap-1"
        >
          {task.detail}
          <ArrowRight size={10} aria-hidden className="text-sky-700" />
        </a>
      </div>
      {task.when ? (
        <span className="text-xs num text-muted tabular-nums flex-shrink-0">{task.when}</span>
      ) : null}
    </li>
  );
}

function describeOverdue(days: number): string {
  if (days >= 60) return `${days}d overdue`;
  if (days >= 30) return `${days}d overdue · trust window slipping`;
  return `${days}d past due`;
}
